import { generateHistoricalPDF } from '../../../../../utils/pdf/generateHistoricalPDF';
import { PDFRecord } from '../../../../../utils/pdf/types';

import { OperatorRow } from '../types';
import { shiftRowDescription,
  formatHHMM,
  formatEstimatedHours,
  formatDateTime,
  groupOrdersByDay,
  dayEntriesLabel,
} from '../utils';

interface ExportContext {
  rows: OperatorRow[];
  teamNameById: Record<string, string>;
  filters: { fromDate: string; toDate: string; states: string[] };
  /** Feature flag `showHoursType`: si OFF, no se muestra el desglose por tipo
   *  de hora (igual que la tabla y el Excel). */
  showHoursType: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString() : '—';

/**
 * Estado de la OT como TEXTO traducido (`common:states.<state>`). En el PDF no
 * hay tag de color — solo texto. Vacío/null → "—".
 */
const stateLabel = (
  state: string | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): string => (state ? t(`states.${state}`, { ns: 'common' }) : '—');

/**
 * Export PDF del informe Gestió Horària.
 *
 * Reutiliza el template canónico `generateHistoricalPDF` (compartido
 * con `DownloadHistoricModal` para Action/Accident detallados):
 *   - Cover page (logo ROIS + título + meta + filtros aplicados).
 *   - Cards por record con header colorado + DataGrid de fields.
 *   - Footer con paginación.
 *
 * Mapeo de mi dominio agregado al shape canónico `PDFRecord`:
 *   roid   → subjectName (nombre del operario en el header del card).
 *   fields → label/value pairs (equipo, tipo, total HH:MM, breakdown
 *            por tipo de hora, detalle de OTs concatenado).
 *   images → [] (mi report no asocia imágenes).
 *
 * El `orderType: 'LaborHours'` no está en `getTypeColor` → cae al
 * default garnet, coherente con el tema lila ROIS.
 */
export async function generateLaborHoursPdf({
  rows,
  teamNameById,
  filters,
  showHoursType,
  t,
}: ExportContext): Promise<void> {
  const records: PDFRecord[] = rows.map((r) => {
    const teamNames = (r.teamIDsSnapshot ?? [])
      .map((id) => teamNameById[id] ?? id)
      .filter(Boolean)
      .join(', ');
    const isExternal = r.kind === 'external-estimated';
    const kindLabel = isExternal
      ? t('forms:timeManagement.kind.external')
      : t('forms:timeManagement.kind.internal');
    const byTypeStr = r.byHourType
      .map((b) => `${b.hourType}: ${formatHHMM(b.minutes)}`)
      .join('  ·  ');

    // Tabla de OTs ("en qué se trabajó") por sujeto. Columnas distintas para
    // externos (con la fórmula Duración × Operarios = Subtotal) e internos.
    const otHeaders = isExternal
      ? [
          t('tables:headers.roid'),
          t('tables:headers.status'),
          t('forms:timeManagement.headers.predefined'),
          t('forms:timeManagement.headers.description'),
          t('forms:workOrder.startDate'),
          t('forms:workOrder.endDate'),
          t('forms:timeManagement.headers.duration'),
          t('forms:timeManagement.headers.operatorsNumber'),
          t('forms:timeManagement.headers.subtotal'),
        ]
      : [
          t('tables:headers.roid'),
          t('tables:headers.status'),
          t('forms:timeManagement.headers.predefined'),
          t('forms:timeManagement.headers.description'),
          t('forms:workOrder.startDate'),
          t('forms:workOrder.endDate'),
          t('tables:headers.totalHours'),
        ];
    // colFlex: la columna Estado (1.5) se inserta tras ROID; el resto se
    // mantiene salvo la descripción, que se reduce un poco para hacerle hueco.
    const otColFlex = isExternal
      ? [2, 1.5, 2, 2, 2, 2, 1.2, 1, 1.3]
      : [2, 1.5, 2, 2.5, 2, 2, 1.3];
    const otRows = r.orders.map((o) =>
      isExternal
        ? [
            o.roid,
            stateLabel(o.state, t) +
              (o.sourceType === 'surveillanceRoute'
                ? ` · ${t('orderTypes.surveillanceRoute', { ns: 'common' })}`
                : o.isSimultaneous
                  ? ` · ${t('simultaneous', { ns: 'common' })}`
                  : ''),
            o.predefined ?? '—',
            // Turno RV: descripción + nº de tramos + descuento por actuaciones.
            shiftRowDescription(o, t, '—'),
            formatDateTime(o.startTime),
            formatDateTime(o.endTime),
            o.durationMinutes != null ? formatHHMM(o.durationMinutes) : '—',
            o.operatorsNumber != null ? String(o.operatorsNumber) : '—',
            formatHHMM(o.minutes),
          ]
        : [
            o.roid,
            stateLabel(o.state, t) +
              (o.sourceType === 'surveillanceRoute'
                ? ` · ${t('orderTypes.surveillanceRoute', { ns: 'common' })}`
                : o.isSimultaneous
                  ? ` · ${t('simultaneous', { ns: 'common' })}`
                  : ''),
            o.predefined ?? '—',
            // Turno RV: descripción + nº de tramos + descuento por actuaciones.
            shiftRowDescription(o, t, '—'),
            formatDateTime(o.startTime),
            formatDateTime(o.endTime),
            formatHHMM(o.minutes),
          ],
    );

    // Tabla-resumen por día (misma agregación que la hoja 4 del Excel).
    const entryLabels = {
      simultaneous: t('simultaneous', { ns: 'common' }),
      surveillanceRoute: t('orderTypes.surveillanceRoute', { ns: 'common' }),
    };
    const dayTable = {
      headers: [
        t('tables:headers.date'),
        t('forms:timeManagement.export.headerOtCount'),
        t('tables:headers.totalHours'),
        t('forms:timeManagement.export.headerOrdersDetail'),
      ],
      rows: groupOrdersByDay(r).map((g) => [
        g.dayLabel,
        String(g.otCount),
        isExternal
          ? formatEstimatedHours(g.totalMinutes)
          : formatHHMM(g.totalMinutes),
        dayEntriesLabel(g.entries, entryLabels),
      ]),
      colFlex: [1.2, 0.8, 1.2, 4.5],
      align: ['left', 'right', 'right', 'left'] as (
        | 'left'
        | 'right'
        | 'center'
      )[],
    };

    return {
      roid: r.subjectName,
      fields: [
        { label: t('tables:headers.team'), value: teamNames || '—' },
        { label: t('tables:headers.type'), value: kindLabel },
        ...(r.operatorsNumber != null
          ? [
              {
                label: t('forms:timeManagement.headers.operatorsNumber'),
                value: String(r.operatorsNumber),
              },
            ]
          : []),
        {
          label: t('tables:headers.totalHours'),
          // Externo: total combinado de todos los operarios (estimado), con
          // marcador `~` igual que la tabla/Excel.
          value: isExternal
            ? `${formatEstimatedHours(r.totalMinutes)} (${t(
                'forms:timeManagement.totalAllOperators',
              )})`
            : formatHHMM(r.totalMinutes),
        },
        // Desglose por tipo de hora: solo si el feature flag está activo
        // (igual que la columna "Tipo de hora" de la tabla y la del Excel).
        ...(showHoursType
          ? [
              {
                label: t('forms:timeManagement.export.headerBreakdown'),
                value: byTypeStr || '—',
              },
            ]
          : []),
      ],
      images: [],
      // Dos tablas con título: resumen por día + detalle de OTs. `tables` es
      // la extensión aditiva de PDFRecord (el `table` legacy queda para el
      // modal de históricos).
      tables: [
        {
          title: t('forms:timeManagement.export.byDayTitle'),
          table: dayTable,
        },
        {
          title: t('forms:timeManagement.export.detailTitle'),
          table: { headers: otHeaders, rows: otRows, colFlex: otColFlex },
        },
      ],
    };
  });

  const filtersSummary = [
    {
      label: t('forms:timeManagement.filters.dateRange'),
      value: `${fmtDate(filters.fromDate)} → ${fmtDate(filters.toDate)}`,
    },
    {
      label: t('tables:headers.status'),
      value:
        filters.states.length > 0
          ? filters.states.map((s) => stateLabel(s, t)).join(', ')
          : '—',
    },
  ];

  await generateHistoricalPDF(
    {
      title: t('forms:timeManagement.export.pdfTitle'),
      orderType: 'LaborHours',
      records,
      generatedAt: new Date().toLocaleString(),
      totalCount: rows.length,
      filtersSummary,
    },
    `gestion-horaria_${filters.fromDate.slice(0, 10)}_${filters.toDate.slice(0, 10)}.pdf`,
  );
}
